import axios from 'axios';
import { useMemo } from 'react';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { loadPdf, renderPdfPageBand } from './pdfRender';
import {
  buildStepHolesPrompt, drillGroupKey, drillGroupParts, drillsHole,
  type HolesContext,
} from './stepHolesPrompt';
import { holesOfLine, lineEnv, swapFrom, swapHand, type HoleLine } from './holeLines';
import { holeSlots, slotLines, type SlotPlace } from './holeSlots';
import type { Solid } from './partSolids';
import { lineEnvOf, withSlotAreas } from './holeArea';
import { picturedViews } from './jointShots';
import type { Job3D } from './threeDApi';
import { matchingRows, useHardwareInteractions, type HardwareInteraction } from './hardwareInteractionsRun';
import {
  boresHole, holeEdges, holeIdBase, holeNumber, holePlace, isCentred, sameUnit, type StepHole, type StepHoles,
} from './stepHoles';
import { namedIn, storedId, withLetter } from './partsCatalogue';
import { useAssemblySteps, type StepBand } from './categoryBands';
import { isHardwareOrTool, isLeftRight, usePartsRun, type PartItem, type Side } from './partsRun';
import type { FaceWord } from './partFaces';
import { getModelSizes } from './partModelSize';
import { fillsHole } from './subStepKinds';
import { pieceContents, pieceNames, useStepsRun, type SubStep, subStepLabel } from './stepsRun';
import { categoriesLoaded, ensureCategories, useCategories } from './catalogCategories';
import { haltGate } from './runHalt';
import { BEAT_MS, HOLES_PHASE, setWorking, watchRemoteStop } from './phaseWorking';

// WHICH HOLES EACH STEP FILLS — one call per GESTURE that puts boring hardware into a part (see
// drillsHole), walked in printed order; stored per step, as every reader expects.
//
// The argument for it being a call of its own rather than a block on the analysis is written at
// the head of stepHolesPrompt: /step-analyze invents the piece ids, so re-running it to fix a
// hole renumbers the step and rewrites every sentence in it.
//
// IN ORDER, AND WAITING, exactly as the analysis is. Two things run through the steps: the hole
// ids, which count within a step but must not collide across the manual, and the holes THEMSELVES
// — a bore revealed in step 2 is referenced by a gesture in step 3, and a call that has not been
// told about it declares a second bore in the same place. A pool cannot know; one after another
// does.

// step category -> what that step found.
export type ByHoles = Record<string, StepHoles>;

type Snapshot = {
  busy: boolean;
  // STOP PRESSED: no call is sent after it, and the one out lands and is kept — it is paid for.
  stopping: boolean;
  running: string[];
  // EVERY GROUP THIS RUN WILL ASK, by groupKey — what it holds now is about to be replaced, so the
  // joint view draws none of it (holes, hardware, notes) until the group's answer lands.
  asking: string[];
  byStep: ByHoles;
  reasoning: Record<string, string>;
  raw: Record<string, string>;
  // Reasoning tokens per gesture, as OpenAI counted them — null where the engine does not say.
  thinking: Record<string, number | null>;
  // WHAT EACH GROUP WAS SENT, by groupKey (see SentRecord).
  sent: Record<string, SentRecord>;
  error: string | null;
  // WHERE A LINE STOPPED SHORT — a part with no model in the product's frame, a fastener with
  // no length (see holeLines) — per group, as groupKey keys it, each note headed by the hole its
  // line enters by. Said under the joint's viewer, never filled in; kept like the reasoning.
  notes: Record<string, string[]>;
  progress: { done: number; total: number } | null;
  startedAt: number | null;
  elapsed: number | null;
};

const holesKey = (docId: string) => `notice.holes.byStep.${docId}`;
const reasonKey = (docId: string) => `notice.holes.reason.${docId}`;
const rawKey = (docId: string) => `notice.holes.raw.${docId}`;
const thinkingKey = (docId: string) => `notice.holes.thinking.${docId}`;
const notesKey = (docId: string) => `notice.holes.notes.${docId}`;
const sentKey = (docId: string) => `notice.holes.sent.${docId}`;

// THE PROMPT A GROUP WAS ACTUALLY SENT, and how many pictures went after its pages — not the one
// the page would build now. The prompt window rebuilt it from the page as it stood, and showed a
// question with outlines a run had sent without: the parts' models were being read again at the
// moment it was asked. The pictures are not kept (they are megabytes); the window draws them again,
// as many as went.
export type SentRecord = { prompt: string; pictures: number };

// EVERY FIELD OF A HOLE IS LISTED HERE, and a new one is listed the day it is written. The same
// rule the sub-step loader carries and for the same reason: a field left out of a mapper lives
// in memory until the first reload and then quietly is not there, which reads as a manual that
// was never asked rather than as a bug.
const loadHoles = (docId: string): ByHoles => {
  const stored = readState<Record<string, Partial<StepHoles>>>(docId, holesKey(docId), {});
  const out: ByHoles = {};
  for (const [category, found] of Object.entries(stored)) {
    out[category] = {
      holes: (found?.holes ?? []).map((h) => ({
        id: h.id,
        at: h.at,
        part: h.part,
        // Passed through as it is: absent means "this part has one hand", which is not the
        // same fact as a left one.
        ...(h.side ? { side: h.side } : {}),
        face: h.face,
        u: h.u,
        v: h.v,
        diameterMm: h.diameterMm ?? null,
        depthMm: h.depthMm ?? null,
        through: !!h.through,
        via: h.via ?? null,
        ...(h.sunkMm ? { sunkMm: h.sunkMm } : {}),
        ...(h.surfaceMm != null ? { surfaceMm: h.surfaceMm } : {}),
        ...(h.woodMm != null ? { woodMm: h.woodMm } : {}),
        ...(h.line ? { line: h.line } : {}),
        ...(h.offWood ? { offWood: true as const } : {}),
        ...(h.unfit ? { unfit: true as const } : {}),
      })),
      fills: found?.fills ?? {},
    };
  }
  return out;
};

const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: {
    busy: false,
    stopping: false,
    running: [],
    asking: [],
    byStep: loadHoles(docId),
    reasoning: readState<Record<string, string>>(docId, reasonKey(docId), {}),
    raw: readState<Record<string, string>>(docId, rawKey(docId), {}),
    thinking: readState<Record<string, number | null>>(docId, thinkingKey(docId), {}),
    sent: readState<Record<string, SentRecord>>(docId, sentKey(docId), {}),
    error: null,
    notes: readState<Record<string, string[]>>(docId, notesKey(docId), {}),
    progress: null,
    startedAt: null,
    elapsed: null,
  },
}));

const update = store.patch;

const markRunning = (docId: string, category: string, on: boolean) => {
  const cur = store.get(docId).running;
  update(docId, { running: on ? [...cur, category] : cur.filter((c) => c !== category) });
};

// EVERYTHING FIND HOLES STORES, as one record: what a run writes per group, what a dropped step
// takes with it, and what Stop puts back (see JointsRun).
export type HeldHoles = Pick<Snapshot, 'byStep' | 'reasoning' | 'raw' | 'thinking' | 'notes' | 'sent'>;
export const heldHoles = (docId: string): HeldHoles => {
  const { byStep, reasoning, raw, thinking, notes, sent } = store.get(docId);
  return { byStep, reasoning, raw, thinking, notes, sent };
};
export function keepHoles(docId: string, h: HeldHoles) {
  writeState(docId, holesKey(docId), h.byStep);
  writeState(docId, reasonKey(docId), h.reasoning);
  writeState(docId, rawKey(docId), h.raw);
  writeState(docId, thinkingKey(docId), h.thinking);
  writeState(docId, notesKey(docId), h.notes);
  writeState(docId, sentKey(docId), h.sent);
  update(docId, h);
}

// THE HOLES, RE-LINKED TO THE ANALYSIS ON SCREEN.
//
// A HOLE IS ITS ID, NOT A LETTER. "H101-A" is the hardware table's: the table names each line's
// holes (TableHole.ids) and Drilling holes places each under its id. What the record also keeps
// is two letters of Phase 4's answer, where the hole is revealed (`at`) and which gestures go
// back into it (`fills`), and those are the letters of the analysis it was FOUND against. Phase 4
// run again rewrites them: tools folded into their gestures, a run of covers made one, and every
// letter after moves. Read by letter, a re-run used to cost every hole of the step (they were
// dropped when the new analysis landed), and with them the drilling they had been paid for.
//
// SO THE LETTERS ARE WORKED OUT AGAIN, each time this is read, from the table: a gesture's row
// (see drillGroupParts) lists the holes it puts its hardware into, so the first gesture in the
// manual whose row lists a hole is where it is revealed, and every later one fills it. The
// hole moves to that gesture's step. Its place, its hardware, its line and its measures are the
// answer's and do not change.
//
// A HOLE NO GESTURE CLAIMS IS KEPT, UNTIMED. It is still in the wood, still its joint's (a joint
// shows the holes its rows name, see holesOfJoint), still drilled: only the sub-step that puts
// its hardware in is unknown. (A gesture that does not say which hand, between a left and a right
// row of one joint, claims both rows' holes: it works them all.) It keeps the letter it was found at only where the analysis is
// still the one the table was read against and still has a hole gesture there; after a re-run
// that letter is another gesture's, and the hole says `at: ''` — no sub-step — rather than that
// one. Nothing is written back: this is what is read, and the record on the server is the
// answer as it was given.
// THE HOLES ONE ROW OF THE HARDWARE TABLE MAKES, out of `all` (every hole found) and `step` (its own
// step's). AND NOT TRUSTED BLINDLY: a row's ids are the ones Find holes gave when the table was made,
// and Find holes numbers again when it runs again. Step 2's right screw row still named H206 (now a
// dowel line) where its screw is H202, so the joint had one screw hole and 2-3's wood button none,
// and a dowel row naming H202 timed that screw with the dowels. A row keeps the holes it names of its
// own hardware, and the ones on their lines (a barrel nut's across a screw's); naming none of its own
// hardware's, it takes them afresh from the step, on its side, with the holes on their lines.
export function rowHoles(r: HardwareInteraction, all: StepHole[], step: StepHole[]): StepHole[] {
  const named = all.filter((h) => r.holes.some((x) => (x.ids ?? []).includes(h.id)));
  const own = (h: StepHole) => h.via != null && r.hardware.includes(h.via);
  const ownLines = new Set(named.filter(own).map((h) => h.line ?? h.id));
  if (!named.length || ownLines.size) return named.filter((h) => own(h) || ownLines.has(h.line ?? h.id));
  const lines = new Set(step.filter((h) => own(h) && (!r.side || h.side === r.side)).map((h) => h.line ?? h.id));
  return step.filter((h) => own(h) && lines.has(h.line ?? h.id));
}

export function relinkHoles(
  byStep: ByHoles,
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  items: PartItem[],
  interactions?: HardwareInteraction[],
): ByHoles {
  // WHERE EACH HOLE IS WORKED, in manual order: step by step, gesture by gesture.
  const claims = new Map<string, { category: string; letter: string }[]>();
  const contents = pieceContents(steps, subSteps);
  const all = Object.values(byStep).flatMap((f) => f.holes);
  // TWO PASSES: the gestures that put hardware in, then — for the holes none of them works — a part
  // laid on with its hardware already in (see drillsHole's `carried`): the front legs onto the frame
  // over their dowels. Never before an insertion: the screws prepared on a rail laid on go in after.
  const claim = (carried: boolean) => steps.forEach((step, i) => {
    const touch = touchOf(steps, i, interactions);
    if (!touch) return;
    const before = new Set(claims.keys());
    for (const sub of drillingSubs(steps, subSteps, items, i, carried)) {
      // ITS ROWS, NOT ONLY ITS ROW (Louis 2026-10-29): a gesture that says no hand, between a left and a
      // right row of one joint, has both — "screw the seat on from underneath", four screws, two
      // each side — and it works the holes of both. Taking its one row left it none, and the
      // Josette's H301 to H304 untimed.
      const { rows } = drillGroupParts(sub, items, contents, touch, carried);
      const own = byStep[step.category]?.holes ?? [];
      for (const id of new Set(rows.flatMap((r) => rowHoles(r, all, own).map((h) => h.id)))) {
        if (carried && before.has(id)) continue;
        const list = claims.get(id) ?? [];
        if (!list.some((c) => c.category === step.category && c.letter === sub.letter)) {
          list.push({ category: step.category, letter: sub.letter });
        }
        claims.set(id, list);
      }
    }
  });
  claim(false);
  claim(true);

  const out: ByHoles = {};
  const recordOf = (category: string) => (out[category] ??= { holes: [], fills: {} });
  const fill = (category: string, letter: string, id: string) => {
    const at = (recordOf(category).fills[letter] ??= []);
    if (!at.includes(id)) at.push(id);
  };
  const gesturesOf = (category: string) => new Set(
    (subSteps[category] ?? []).filter((sub) => fillsHole(sub.kind)).map((sub) => sub.letter),
  );
  let changed = false;
  // THE ANALYSIS HAS CHANGED SINCE THE TABLE: a stored letter names another gesture now.
  const rerun = !!interactions?.some((r) => r.stale);
  for (const [category, found] of Object.entries(byStep)) {
    const gestures = rerun ? new Set<string>() : gesturesOf(category);
    for (const hole of found.holes) {
      const worked = claims.get(hole.id);
      if (worked?.length) {
        const [first, ...later] = worked;
        if (first.category !== category || first.letter !== hole.at) changed = true;
        recordOf(first.category).holes.push(first.letter === hole.at ? hole : { ...hole, at: first.letter });
        later.forEach((c) => fill(c.category, c.letter, hole.id));
      } else if (gestures.has(hole.at)) {
        recordOf(category).holes.push(hole);
      } else {
        changed = true;
        recordOf(category).holes.push({ ...hole, at: '' });
      }
    }
    // THE GESTURES THAT GO BACK INTO A HOLE NO ROW LISTS, as they were found — the table's own
    // holes have theirs from the table, above.
    for (const [letter, ids] of Object.entries(found.fills)) {
      for (const id of ids) {
        if (claims.get(id)?.length) continue;
        if (gestures.has(letter)) fill(category, letter, id);
        else changed = true;
      }
    }
  }
  // WHAT THE TABLE MAKES A GESTURE FILL that the record did not say, which is every hole on a
  // manual re-analysed since: the same object back where nothing moved, which is every
  // ordinary read, or the memos downstream refresh for nothing.
  const fillsOf = (m: ByHoles) => JSON.stringify(Object.entries(m).map(([c, f]) => [c, f.fills]).sort());
  if (!changed && Object.keys(out).length === Object.keys(byStep).length && fillsOf(out) === fillsOf(byStep)) {
    return byStep;
  }
  return out;
}

// THE KEY ONE CALL'S REASONING AND RAW REPLY ARE KEPT UNDER: its step and its group's letters.
export const gestureKey = (category: string, letter: string) => `${category}#${letter}`;
export const groupKey = (category: string, group: SubStep[]) =>
  gestureKey(category, group.map((s) => s.letter).join('+'));

// THE CALLS OF ONE STEP: its drilling gestures grouped by what they drill and what they drive
// into it (see drillGroupKey), each group in manual order and the groups in the order of their
// first gesture — so the barrel nuts are found before the screws that thread into them.
//
// NONE WITHOUT THE HARDWARE TABLE, and none for a gesture no row of it names — see drillGroupKey.
export function drillingGroups(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  items: PartItem[],
  idx: number,
  interactions?: HardwareInteraction[],
): SubStep[][] {
  const contents = pieceContents(steps, subSteps);
  const touch = touchOf(steps, idx, interactions);
  if (!touch) return [];
  const groups = new Map<string, SubStep[]>();
  for (const sub of drillingSubs(steps, subSteps, items, idx)) {
    const key = drillGroupKey(sub, items, contents, touch);
    if (key != null) groups.set(key, [...(groups.get(key) ?? []), sub]);
  }
  return [...groups.values()];
}

// THE DRILLING GESTURES NO ROW OF THE HARDWARE TABLE NAMES, which Find holes therefore does not
// ask about — said, so a part the table left out is seen missing rather than quietly holeless.
// Each as its label ("2C") and the hardware it brings.
export function unmatchedDrilling(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  items: PartItem[],
  interactions: HardwareInteraction[],
): { label: string; bores: string[] }[] {
  const contents = pieceContents(steps, subSteps);
  return steps.flatMap((step, idx) => {
    const touch = touchOf(steps, idx, interactions)!;
    return drillingSubs(steps, subSteps, items, idx).flatMap((sub) => {
      const { rows, bores } = drillGroupParts(sub, items, contents, touch);
      return rows.length ? [] : [{ label: subStepLabel(step.num, sub.letter), bores }];
    });
  });
}

// THE HOLES ONE GROUP DECLARED, and the ones it re-entered: its step's holes at its letters, and
// the ones filled at them.
export function holesOfGroup(byStep: ByHoles, category: string, group: SubStep[]): StepHole[] {
  const found = byStep[category];
  if (!found) return [];
  const letters = new Set(group.map((s) => s.letter));
  const filled = new Set(Object.entries(found.fills)
    .filter(([letter]) => letters.has(letter)).flatMap(([, ids]) => ids));
  const all = Object.values(byStep).flatMap((f) => f.holes);
  return [
    ...found.holes.filter((h) => letters.has(h.at)),
    ...all.filter((h) => filled.has(h.id) && !letters.has(h.at)),
  ].filter((h, k, list) => list.findIndex((o) => o.id === h.id) === k);
}

// THE GESTURES OF ONE STEP THAT GET A CALL, in order — see drillsHole.
export function drillingSubs(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  items: PartItem[],
  idx: number,
  carried = false,
): SubStep[] {
  const step = steps[idx];
  if (!step) return [];
  const contents = pieceContents(steps, subSteps);
  return (subSteps[step.category] ?? []).filter((sub) => drillsHole(sub, items, contents, carried));
}

// The hardware table's rows, read for the step at `idx` — see drillGroupParts.
export const touchOf = (steps: StepBand[], idx: number, interactions?: HardwareInteraction[]) =>
  (interactions && steps[idx] ? { interactions, step: steps[idx].num } : undefined);

// THE NEXT FREE HOLE ID IN A STEP, past every one it already has: H101, H102… for step 1. Ids of
// other steps' ranges are not this step's to step past.
const nextHoleId = (stepNo: number, ids: string[]): string => {
  const base = Number(holeIdBase(stepNo).slice(1));
  const taken = ids.map(holeNumber)
    .filter((n) => Number.isFinite(n) && n >= base && n < base + 99);
  return `H${Math.max(base, ...taken.map((n) => n + 1))}`;
};

// THE CONTEXT ONE GESTURE'S CALL IS GIVEN, built HERE and used by both the run and the badge — so
// the preview is byte-for-byte what is sent, which is the contract every prompt in this app
// keeps.
//
// `sameStep` is the holes this step has already declared, at the gestures before this one: the
// run passes what it has found so far, the badge what is stored. Together with every earlier
// step's they are what the call is told exists — filtered to the parts this gesture can bore,
// which are the only ones it could reuse.
export function holesContextFor(
  docId: string,
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  items: PartItem[],
  byStep: ByHoles,
  idx: number,
  letters: string[],
  sameStep: StepHole[],
  // THE PART'S 3D MODEL, by part id — what is pictured for the call. Absent, nothing is.
  meshOf?: MeshOf,
  // Ids this step's holes at OTHER gestures already hold, where only this one is being asked
  // again: they stay, so a new hole must not take one of them.
  taken: StepHole[] = [],
  // The hardware table's rows, which narrow the parts to the ones the hardware touches.
  interactions?: HardwareInteraction[],
): HolesContext | null {
  const step = steps[idx];
  const subs = (subSteps[step?.category ?? ''] ?? []).filter((s) => letters.includes(s.letter));
  if (!step || !subs.length) return null;
  const contents = pieceContents(steps, subSteps);
  // THE GROUP'S PARTS, every gesture's, each once and in id order.
  const union = (ids: string[][]) => [...new Set(ids.flat())].sort((a, b) => Number(a) - Number(b));
  const touch = touchOf(steps, idx, interactions);
  const read = subs.map((sub) => drillGroupParts(sub, items, contents, touch));
  const drilled = union(read.map((r) => r.drilled));
  const rows = [...new Set(read.flatMap((r) => r.rows))];
  const earlier: StepHole[] = [];
  for (let i = 0; i < idx; i++) earlier.push(...(byStep[steps[i].category]?.holes ?? []));
  const inScope = new Set(drilled);
  const models = getModelSizes(docId);
  // WHAT EACH GESTURE BRINGS, through the pieces — which slot's hardware it puts in.
  const brought = subs.map((sub) => {
    const parts = sub.parts ?? [];
    const added = parts.some((u) => u.role) ? parts.filter((u) => u.role === 'added') : parts;
    return [...new Set(added.flatMap((u) => contents.get(u.id) ?? [u.id]))];
  });
  // THE TABLE'S IDS, which every slot takes (see TableHole.ids); a number of the run's own only
  // past them.
  const tableIds = (interactions ?? []).flatMap((r) => r.holes.flatMap((h) => h.ids ?? []));
  const first = nextHoleId(step.num, [...sameStep, ...taken].map((h) => h.id).concat(tableIds));
  const slots = holeSlots(rows, subs, read.map((r) => r.row), brought,
    meshOf ? lineEnv(items, models, drilled, meshOf) : null, first);
  return {
    step: step.num,
    subs,
    items,
    // English: this is prompt copy.
    pieces: pieceNames(steps, subSteps, 'en'),
    drilled,
    known: [...earlier, ...sameStep].filter((h) => inScope.has(h.part)),
    // WHAT EACH PART'S MESH MEASURES — the box every face in this prompt is a rectangle of.
    // See partModelSize, which is where it is told apart from the size the manual gives.
    models,
    first,
    rows,
    pictured: [],
    slots,
  };
}

// THE HOLES A STEP'S EARLIER GROUPS DECLARED — what a group is told exists in its own step when it
// is asked on its own (the badge, a one-group run), read from what is stored.
export const holesBefore = (found: StepHoles | undefined, groups: SubStep[][], at: number) => {
  const before = new Set(groups.slice(0, at).flat().map((s) => s.letter));
  return (found?.holes ?? []).filter((h) => before.has(h.at));
};

// Which 3D model a part of the list is drawn with, or null — see FindHoles, which resolves it
// the way Phase 7 does.
export type MeshOf = (partId: string) => Job3D | null;

// THE ONE REPAIR MADE TO AN ANSWER, and the argument for making it rather than dropping.
//
// `u` and `v` are each measured from one of the two edges that bound THAT axis of the face, and
// the prompt lists which those are per face. A model that names a real edge under the wrong key
// — the v-edge given for u — has read the drawing correctly and filed the answer crosswise, and
// the two are distinguishable: if each reading is legal for the OTHER axis, they are swapped.
//
// Nothing else is touched. A pair that is wrong in any other way is left exactly as given and
// comes out unplaceable (see holePlace), which is a hole a reader can see is wrong rather than
// one silently moved to somewhere plausible.
// A HOLE IS BORED IN A PLANK, and this is where one filed against an ASSEMBLY is put right.
//
// The prompt says it twice — bore it in the supplied component part, never in the piece the
// step is building — and a model still answers "in 106, the frame with the left side rail"
// now and then. It used to be DROPPED here, silently: the bore was found, paid for, and
// deleted, and all a reader saw was a gap in the hole numbers.
//
// WHAT THE CONTAINMENT MAP CAN AND CANNOT SETTLE. `contents` says what each piece is made of,
// down to supplied parts, and it is already built — the prompt sends it, because "which plank"
// is answerable from it and from nothing else. But a piece is a SET and a bore is in exactly
// one object, so it settles the question only where the set holds ONE component part: the
// hardware in it takes no bores anybody drills, and "106 = frame + left side rail + barrel
// nut" resolves to the frame the moment the rail is not there. Where two planks are in it,
// nothing here can choose, and guessing would be worse than the drop was.
//
// SO THE UNRESOLVABLE ONE IS KEPT, ON THE PIECE. It draws in Phase 4's reading as a bore
// nobody could place — see HolesCell — and it matches no part, so nothing tries to drill it.
// Which is the difference that matters: an answer that is wrong and visible can be argued
// with, and one that was thrown away cannot.
const placeInPart = (items: PartItem[], contents: Map<string, string[]>) =>
  (hole: StepHole): StepHole => {
    if (items[Number(hole.part)]) return hole;
    const planks = (contents.get(hole.part) ?? [])
      .filter((id) => items[Number(id)] && !isHardwareOrTool(items[Number(id)]));
    return planks.length === 1 ? { ...hole, part: planks[0] } : hole;
  };

// THE BORES NOBODY COULD PLACE, over the whole manual — the count the phase shows so that a
// hole answered against an assembly is a number on the screen rather than a silence. Same test
// as everywhere: is there a supplied part at that index.
// AND THE BORES ANSWERED FOR HARDWARE THAT MAKES NONE — a wood button, a washer, a felt pad.
// The prompt now lists those apart and says outright that they bore nothing, so this is the
// backstop rather than the rule; kept and counted rather than dropped, for the same reason
// every other refusal here is: an answer that is wrong and on the screen can be argued with.
//
// They cannot be cut either way — holeWidthOf gives them no width — so what this adds is the
// REASON, where the batch button's list would otherwise file them under "no width" and send
// somebody looking for a missing measurement.
// AND NOTHING UNTIL THE ONTOLOGY HAS LANDED. boresHole answers false for every part while the
// category list is unfetched — it cannot tell "not hardware" from "not looked up yet" — so this
// counted EVERY hole in the manual and reported them as bores for hardware that makes none. The
// number flapped with the fetch rather than with the answers: 15, then none, then 20, on three
// readings of one unchanged document.
//
// Empty is the right nothing here. This is a count of findings, and "no findings yet" and "no
// findings" both mean the line stays off the page — where a number would be a claim, and the
// claim would be false.
export const borelessHoles = (byStep: ByHoles, items: PartItem[]): StepHole[] =>
  (!categoriesLoaded() ? [] : Object.values(byStep).flatMap((found) => found.holes.filter(
    (h) => h.via != null && !boresHole(items[Number(h.via)]),
  )));

export const unplacedHoles = (byStep: ByHoles, items: PartItem[]): StepHole[] =>
  Object.values(byStep).flatMap((found) =>
    found.holes.filter((h) => !items[Number(h.part)]));

const repairEdges = (items: PartItem[]) => (hole: StepHole): StepHole => {
  const edges = holeEdges(hole.face, items[Number(hole.part)]?.size);
  if (!edges) return hole;
  const fits = (at: StepHole['u'], legal: FaceWord[]) => isCentred(at) || legal.includes(at.from);
  if (fits(hole.u, edges.u) && fits(hole.v, edges.v)) return hole;
  if (fits(hole.u, edges.v) && fits(hole.v, edges.u)) return { ...hole, u: hole.v, v: hole.u };
  return hole;
};

// THE HOLES A CALL'S LINES MAKE, numbered on from the group's first id. A hole landing where one
// is already declared in that part — a line asked twice — is that hole, filled at this gesture,
// not a second bore in the same place.
//
// A LINE INTO A MIRROR PAIR IS TWO LINES, one per unit (see holesOfLine): the call gives a pair
// one set of lines, and the product has two rails, each with its own screws. So each unit's
// holes are their own, with their own ids and their own notes — and two holes on different
// units are never the same hole, though both are one place in the one file (the drill cuts
// that place once: see HolesTable's batch).
function lineHoles(
  lines: HoleLine[],
  ctx: HolesContext,
  run: {
    items: PartItem[];
    contents: Map<string, string[]>;
    within: (at: string | undefined) => string;
    shift: (id: string) => string;
    meshOf?: MeshOf;
    found: StepHoles;
    known: StepHole[];
    // This group's notes, filled here.
    notes: string[];
    solids: Map<string, Solid>;
  },
): StepHole[] {
  const { items, contents, within, shift, found, known, notes } = run;
  const env = lineEnvOf(ctx, run.meshOf, run.solids);
  const name = (id: string) => {
    const p = items[Number(id)];
    return withLetter(namedIn(p, 'en') || `#${id}`, p?.letter);
  };
  const dimsOf = (part: string) => {
    const box = env.placed.get(part);
    return box
      ? { x: box.max[0] - box.min[0], y: box.max[1] - box.min[1], z: box.max[2] - box.min[2] }
      : ctx.models[part];
  };
  const same = (a: Omit<StepHole, 'id'>, b: StepHole) => {
    if (a.part !== b.part || a.face !== b.face || !sameUnit(a.side, b.side)) return false;
    const pa = holePlace({ ...a, id: '' }, dimsOf(a.part));
    const pb = holePlace(b, dimsOf(b.part));
    return !!pa && !!pb && Math.abs(pa.u - pb.u) < 1.5 && Math.abs(pa.v - pb.v) < 1.5;
  };
  // Past the group's first id, past the ids its slots were given beforehand (each kept for its entry
  // hole, a mirrored slot's other hand's too), and past anything this call answered as a plain hole.
  let next = Math.max(Number(ctx.first.slice(1)),
    ...[...ctx.slots.flatMap((sl) => [sl.id, ...sl.ids.map((x) => x.id),
      ...(sl.mirror ? [sl.mirror.id, ...sl.mirror.ids.map((x) => x.id)] : [])]), ...found.holes.map((h) => h.id)]
      .map((id) => holeNumber(id) + 1).filter(Number.isFinite));
  const given = new Set<string>();
  const out: StepHole[] = [];
  for (const raw of lines) {
    const part = shift(raw.part);
    // A LINE ENTERING A PAIR'S RIGHT UNIT, SAID IN THE LEFT ONE'S FRAME — the pair's holes are
    // stored in the left unit's words (see StepHole.side), so 'left' and 'right' swap, in the face,
    // the edges and the nut's face. Only where the part entered IS a pair: a right-hand screw
    // into the frame both rails meet is said in the frame's own words, and swapping them put it
    // on top of the left-hand one. It was the server's, which cannot tell a pair from a frame.
    const pair = isLeftRight(items[Number(part)]);
    const turn = pair && raw.side === 'right';
    const line: HoleLine = {
      ...(turn ? { ...raw, face: swapHand(raw.face), u: swapFrom(raw.u), v: swapFrom(raw.v) } : raw),
      at: within(raw.at),
      via: shift(raw.via),
      part,
      // AND A HAND ONLY ON A PAIR: a frame has none; the rail its line reaches says which one.
      side: pair ? raw.side : undefined,
      nut: raw.nut ? {
        ...raw.nut, at: within(raw.nut.at), via: shift(raw.nut.via), face: turn ? swapHand(raw.nut.face) : raw.nut.face,
      } : null,
    };
    // The entry said as a hole is, so it is put in a plank and its edges read as a hole's are.
    const said = repairEdges(items)(placeInPart(items, contents)({
      ...line, id: '', diameterMm: null, depthMm: null, through: false,
    } as StepHole));
    // EVERY PIECE ITS OWN LINE, WITH ITS HAND: a line into a pair makes that hand's holes. One with
    // no hand (an answer from before every piece was asked for) makes both, as it always did.
    const hands: (Side | undefined)[] = !isLeftRight(items[Number(said.part)]) ? [undefined]
      : line.side ? [line.side] : ['left', 'right'];
    for (const hand of hands) {
      const made = holesOfLine({ ...line, part: said.part, u: said.u, v: said.v }, env, name, hand);
      // The hole the line enters by names it: its notes are about that one piece of hardware.
      // AND ONLY A LINE THAT MADE THAT HOLE SAYS ANYTHING. A line the model repeats — once per
      // sub-step of one screw — or sends again for hardware another step placed makes no hole of
      // its own; its notes were being filed a second time, under a hole in another table.
      let entry = '';
      let ownsEntry = false;
      const fresh: StepHole[] = [];
      // TWO SLOTS OF ONE ANSWER ON ONE SPOT: the call gave H104-A H103-A's very place, and its line was
      // taken for H103-A's reused — H104 and its mirror went undrilled, said nowhere. Reuse is a gesture
      // repeating a hole another one made; two slots are two pieces of hardware, and one spot cannot
      // hold both. Said under the slot, and its line makes nothing.
      let clash: string | null = null;
      for (const h of made.holes) {
        const there = [...known, ...found.holes, ...out, ...fresh].find((k) => same(h, k));
        if (there && h === made.holes[0] && raw.id && out.includes(there) && there.line !== raw.id) {
          clash = there.line ?? there.id;
          break;
        }
        if (there) {
          const at = (found.fills[h.at] ??= []);
          if (!at.includes(there.id)) at.push(there.id);
          entry ||= there.id;
          continue;
        }
        // THE ENTRY TAKES THE ID ITS SLOT WAS ASKED UNDER, once — a slot made into both hands of a pair
        // numbers the second on.
        const mine = !entry && raw.id && !given.has(raw.id) ? raw.id : null;
        // AND EVERY OTHER HOLE THE ID THE TABLE GAVE IT — the same part, for the same hardware.
        const tabled = mine ? null
          : raw.ids?.find((x) => x.part === h.part && x.via === h.via && !given.has(x.id))?.id ?? null;
        if (mine) given.add(mine);
        if (tabled) given.add(tabled);
        const id = mine ?? tabled ?? `H${next++}`;
        if (!entry) ownsEntry = true;
        entry ||= id;
        fresh.push({ ...h, id });
      }
      if (clash) {
        notes.push(`${raw.id} · given the same place as ${clash}, so it would be the same hole: not drilled`);
        continue;
      }
      out.push(...fresh.map((h) => ({ ...h, line: entry })));
      if (ownsEntry) notes.push(...made.notes.map((n) => `${entry} · ${n}`));
    }
  }
  return out;
}

// STOP THIS RUN, and only this one — see Snapshot.stopping.
export function stopStepHoles(docId: string) {
  if (store.get(docId).busy) update(docId, { stopping: true });
}

// Find the holes of every step, one call at a time in printed order — or of ONE GROUP, `only` =
// {step index, its letters}, which leaves every other group's holes where they are.
export async function findStepHoles(
  docId: string,
  file: Blob,
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  items: PartItem[],
  model: string,
  t: (k: string) => string,
  only?: { idx: number; letters: string[] },
  meshOf?: MeshOf,
  interactions?: HardwareInteraction[],
  // STOP'S ABORT, from the Holes & covers run: the call out is dropped, not waited for.
  signal?: AbortSignal,
) {
  const entry = store.getEntry(docId);
  if (entry.snapshot.busy) return;
  if (!model || !steps.length) {
    update(docId, { error: t('notice.cat2.noRun') });
    return;
  }
  // THE ONTOLOGY BEFORE THE FIRST PROMPT, AND WAITED FOR.
  //
  // isHardwareOrTool resolves an unfetched ontology to "component" — groupOfCategory's default,
  // which is the right one for a category nobody has classified and the wrong one for a
  // category nobody has LOOKED UP yet. The two are indistinguishable from inside it: an empty
  // list is both. So a run that started before that fetch landed described every screw, washer
  // and barrel nut as a component part somebody drills, printed neither the list of hardware a
  // bore is FOR nor the list of things that bore nothing, and left the rules pointing at two
  // headings that were not in the prompt.
  //
  // catalogCategories says this in its own words about exactly this kind of caller: silence is
  // a fine answer for a sort order and a false one for a prompt. stepsRun and stepHoles both
  // guard on categoriesLoaded; this call was the one that did not.
  await ensureCategories();

  const order = only != null ? [only.idx] : steps.map((_, i) => i);
  // ONE UNIT PER CALL, which is one per group of drilling gestures.
  const total = only != null ? 1
    : order.reduce((n, i) => n + drillingGroups(steps, subSteps, items, i, interactions).length, 0);
  const gate = haltGate(docId);
  const halted = () => gate() || store.get(docId).stopping || !!signal?.aborted;
  const startedAt = Date.now();

  // OVER WHAT IS THERE, never instead of it — the same rule the analysis keeps. A run that dies
  // halfway leaves the answers it never got to rather than taking them with it, where wiping
  // first would tell every other reader of this shared manual that the holes had never been
  // looked for, for as long as the run took.
  // RE-LINKED TO THE ANALYSIS, like every other reader (see relinkHoles). This map is two things at
  // once: what the next step's call is told already exists, and the map this run writes back — so
  // a run after a Phase 4 re-run writes the holes back under the letters they have now.
  const byStep: ByHoles = { ...relinkHoles(entry.snapshot.byStep, steps, subSteps, items, interactions) };
  const reasoning: Record<string, string> = { ...entry.snapshot.reasoning };
  const raw: Record<string, string> = { ...entry.snapshot.raw };
  const thinking: Record<string, number | null> = { ...entry.snapshot.thinking };
  const sent: Record<string, SentRecord> = { ...entry.snapshot.sent };

  const notes: Record<string, string[]> = { ...entry.snapshot.notes };
  update(docId, {
    busy: true,
    running: [],
    asking: order.flatMap((i) => drillingGroups(steps, subSteps, items, i, interactions)
      .filter((g) => !only || g.map((x) => x.letter).join('+') === only.letters.join('+'))
      .map((g) => groupKey(steps[i].category, g))),
    error: null,
    progress: { done: 0, total },
    startedAt,
    elapsed: null,
  });

  // Phase 6's work now, where the holes are found and drilled — see FindHoles.
  const atWork = (done: number) => setWorking(docId, { phase: HOLES_PHASE, done, total });
  atWork(0);
  const beat = window.setInterval(
    () => atWork(store.get(docId).progress?.done ?? 0), BEAT_MS,
  );
  // AND STOPPED FROM ANOTHER TAB, where one asks (see watchRemoteStop).
  const unwatch = watchRemoteStop(docId);

  let loadingTask: PDFDocumentLoadingTask | null = null;
  let done = 0;
  try {
    const buf = await file.arrayBuffer();
    loadingTask = loadPdf(buf);
    const doc: PDFDocumentProxy = await loadingTask.promise;

    // THE STEP'S PAGES, once for all its gestures; and each part's six renders, once for the run.
    const renders = new Map<string, Awaited<ReturnType<typeof picturedViews>>>();
    // AND EACH PART'S REAL SURFACE, once for the run — what the lines are measured on.
    const solids = new Map<string, Solid>();
    const tried = new Set<string>();
    const viewsOf = async (ctx: HolesContext) => {
      const key = JSON.stringify(ctx.slots.map((sl) => [sl.via, sl.part, sl.side, sl.face, sl.next, sl.patch, sl.area]));
      if (!renders.has(key)) renders.set(key, await picturedViews(ctx, meshOf));
      return renders.get(key)!;
    };

    const ask = async (idx: number) => {
      const step = steps[idx];
      const every = drillingGroups(steps, subSteps, items, idx, interactions);
      const sameLetters = (g: SubStep[]) => g.map((x) => x.letter).join('+') === only?.letters.join('+');
      const groups = only ? every.filter(sameLetters) : every;
      // NOTHING TO ASK ABOUT, AND SO NOTHING ASKED: no gesture of this step puts boring hardware
      // into a part — it stands panels against each other, or makes fasteners in the hand.
      if (!groups.length) return;

      const images: string[] = [];
      const pages: number[] = [];
      for (const page of step.pages) {
        if (halted()) break;
        const band = step.bands[page] ?? { top: 0, bottom: 1 };
        const image = await renderPdfPageBand(doc, page, band.top, band.bottom);
        if (image) {
          images.push(image);
          pages.push(page);
        }
      }
      if (halted() || !images.length) {
        done += groups.length;
        update(docId, { progress: { done, total } });
        return;
      }

      // THIS STEP'S ANSWER, BUILT UP GESTURE BY GESTURE — each call told what the ones before it
      // declared, which is how "the remaining hole" finds the first one.
      //
      // OVER THE OLD ONE, not instead of it: what the step held at a gesture not yet asked again
      // stays until that gesture's call lands, so a run stopped halfway leaves the rest as it was.
      const found: StepHoles = { holes: [], fills: {} };
      const previous = byStep[step.category] ?? { holes: [], fills: {} };
      const asked = new Set<string>();
      const notYet = () => previous.holes.filter((h) => !asked.has(h.at));
      // ONE GROUP ASKED AGAIN: told what its step's earlier groups declared, from what is stored —
      // as a run in order would have it — and numbering past every hole the step keeps.
      const mine = new Set(only?.letters ?? []);
      const before = only ? holesBefore(previous, every, every.findIndex(sameLetters)) : [];
      const kept = only ? previous.holes.filter((h) => !mine.has(h.at)) : [];
      for (const group of groups) {
        if (halted()) return;
        const letters = group.map((x) => x.letter);
        const key = groupKey(step.category, group);
        markRunning(docId, key, true);
        try {
          let ctx = holesContextFor(
            docId, steps, subSteps, items, byStep, idx, letters, [...before, ...found.holes],
            meshOf, kept, interactions,
          );
          if (!ctx) continue;
          // NOTHING TO PLACE, NOTHING ASKED: the hardware table lists no hole where this group's
          // hardware enters (see holeSlots). Said, and what the group held stays.
          if (!ctx.slots.length) {
            notes[key] = ['the hardware table lists no hole for this joint\'s hardware to enter by, so none was asked'];
            keepHoles(docId, {
              byStep: { ...byStep }, notes: { ...notes },
              reasoning: { ...reasoning }, raw: { ...raw }, thinking: { ...thinking }, sent: { ...sent },
            });
            continue;
          }
          // NOT ASKED WITHOUT WHERE THE PARTS STAND: a part the holes go in or on into with no
          // place in the product has no outline to place them in and no wood to follow them
          // through, and the call guesses off the drawing — a 482 mm frame placed 30 mm from its
          // edge, in the air. Said, and what the group held stays.
          const placed = meshOf ? lineEnv(items, ctx.models, ctx.drilled, meshOf).placed : new Map();
          const unplaced = [...new Set(ctx.slots.flatMap((sl) => (sl.next ? [sl.part, sl.next] : [sl.part])))]
            .filter((id) => !placed.has(id));
          if (unplaced.length) {
            const named = unplaced.map((id) => withLetter(namedIn(items[Number(id)], 'en') || `#${id}`, items[Number(id)]?.letter));
            notes[key] = [`Not asked: ${named.join(', ')} ${unplaced.length > 1 ? 'have' : 'has'} no place in the `
              + 'product (no piece of the Whole product model matched, or the models had not loaded), so there is '
              + 'no outline to place the holes in — see Phase 5 › Whole product'];
            keepHoles(docId, {
              byStep: { ...byStep }, notes: { ...notes },
              reasoning: { ...reasoning }, raw: { ...raw }, thinking: { ...thinking }, sent: { ...sent },
            });
            continue;
          }
          // WHERE IN EACH OUTLINE A HOLE CAN GO, tried on the parts' solids (see holeArea); then the
          // meshes the holes go in, after the pages — see picturedViews.
          ctx = await withSlotAreas(ctx, meshOf, solids, tried);
          if (halted()) return;
          const drawn = await viewsOf(ctx);
          if (halted()) return;
          ctx = { ...ctx, pictured: drawn.shown };

          type Reply = {
            holes?: StepHole[];
            lines?: HoleLine[];
            // THE SLOTS' PLACES, which is what is asked now (see holeSlots).
            places?: SlotPlace[];
            fills?: { at: string; id: string }[];
            reasoning?: string;
            raw?: string;
            thinking?: number | null;
          };
          const prompt = buildStepHolesPrompt(ctx);
          const send = async (text: string) => (await axios.post<Reply>('/api/notice/step-holes', {
            model,
            images: [...images, ...drawn.views.map((v) => v.url)],
            pages,
            step: step.num,
            prompt: text,
          }, { signal })).data;

          // THE IDS COME BACK ONE HIGHER THAN THEY ARE STORED, because that is how they were
          // offered (see shownId). Put back at the moment they land, exactly as the analysis
          // does it, so everything downstream goes on counting from zero.
          const shift = (id: string) => storedId(id, items.length);
          const contents = pieceContents(steps, subSteps);
          // FILED UNDER THE GESTURE IT NAMES, where that is one of the group's; a letter from
          // outside it goes under the group's first, since the call was asked about these alone.
          const within = (at: string | undefined) => {
            // "2" or "3-2": the number after the step's, where the reply wrote both.
            const l = (at || '').toLowerCase().split('-').pop() ?? '';
            return letters.includes(l) ? l : letters[0];
          };
          const data = await send(prompt);
          {
            for (const f of data.fills ?? []) {
              if (f.id) (found.fills[within(f.at)] ??= []).push(f.id);
            }
            found.holes.push(...(data.holes ?? [])
              .map((h) => ({
                ...h,
                at: within(h.at),
                part: shift(h.part),
                via: h.via ? shift(h.via) : null,
              }))
              // A bore filed against an assembly is resolved to the plank inside it where the
              // piece holds exactly one, and kept on the piece where it holds two — see
              // placeInPart, which argues both halves. Before repairEdges, which reads the part's
              // own size and would otherwise read the wrong part's.
              .map(placeInPart(items, contents))
              .map(repairEdges(items)));
            // AND THE LINES — the slots, answered, and any given whole — each turned into every hole
            // along it: see holeLines. A slot left without a place is said.
            const { lines: placed, missing } = slotLines(ctx.slots, data.places ?? []);
            notes[key] = missing.map((id) => `${id}: no place was given for it, so it has no hole`);
            found.holes.push(...lineHoles([...(data.lines ?? []), ...placed], ctx, {
              items, contents, within, shift, meshOf, found, notes: notes[key], solids,
              known: [...ctx.known, ...kept],
            }));
          }
          reasoning[key] = data.reasoning || '';
          raw[key] = data.raw || '';
          sent[key] = { prompt, pictures: drawn.views.length };
          thinking[key] = typeof data.thinking === 'number' ? data.thinking : null;
          letters.forEach((l) => asked.add(l));

          // The new answer numbers from H101 again, so an old hole whose id it has taken goes
          // now rather than when its own gesture is asked: two holes under one id is two answers.
          const taken = new Set(found.holes.map((h) => h.id));
          const still = notYet().filter((h) => !taken.has(h.id));
          const merged: StepHoles = {
            holes: [...found.holes, ...still],
            fills: {
              ...Object.fromEntries(Object.entries(previous.fills).filter(([l]) => !asked.has(l))),
              ...found.fills,
            },
          };
          byStep[step.category] = {
            holes: merged.holes,
            fills: merged.fills,
          };
          keepHoles(docId, {
            byStep: { ...byStep },
            notes: { ...notes },
            reasoning: { ...reasoning },
            raw: { ...raw },
            thinking: { ...thinking },
            sent: { ...sent },
          });
        } catch (e) {
          // ABORTED BY STOP: not a failure, and nothing to say — the joint is being put back.
          if (axios.isCancel(e)) return;
          // One call's failure is one call's failure: a 502 on 1C must not cost 1D to 3F.
          const msg =
            (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error) ||
            (e instanceof Error && e.message) ||
            t('notice.err.generic');
          update(docId, { error: msg });
        } finally {
          markRunning(docId, key, false);
          // Answered (or failed): what it holds is the answer now, and is drawn again.
          update(docId, { asking: store.get(docId).asking.filter((k) => k !== key) });
          update(docId, { progress: { done: ++done, total } });
        }
      }
    };

    for (const idx of order) {
      if (halted()) break;
      await ask(idx);
    }
  } catch (e) {
    const msg = axios.isCancel(e) ? null :
      (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error) ||
      (e instanceof Error && e.message) ||
      t('notice.err.generic');
    update(docId, { error: msg });
  } finally {
    window.clearInterval(beat);
    unwatch();
    setWorking(docId, null);
    update(docId, {
      busy: false,
      stopping: false,
      running: [],
      asking: [],
      progress: null,
      startedAt: null,
      elapsed: Math.round((Date.now() - startedAt) / 1000),
    });
    void loadingTask?.destroy();
  }
}

// THE HOLES AS RE-LINKED TO THE ANALYSIS ON SCREEN — see relinkHoles. Here rather than at each
// reader, which is where it would otherwise have to be: Phase 4's table, its two counts, its
// prompt preview and Phase 5's mesh panel all ask this store the same question, and a reading
// done at three of the four is the fourth one drilling a bore the manual no longer says is there.
export function useStepHoles(docId: string) {
  const snap = store.useSnapshot(docId);
  const { subSteps } = useStepsRun(docId);
  const { steps } = useAssemblySteps(docId);
  const { items } = usePartsRun(docId);
  const table = useHardwareInteractions(docId);
  const interactions = useMemo(() => matchingRows(table, steps, subSteps), [table, steps, subSteps]);
  // AND THE ONTOLOGY: before it lands no part bores a hole (see boresHole), so no gesture claims one,
  // and every hole read as untimed — kept so until something else changed.
  const categories = useCategories();
  return useMemo(() => {
    const byStep = relinkHoles(snap.byStep, steps, subSteps, items, interactions);
    return byStep === snap.byStep ? snap : { ...snap, byStep };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap, steps, subSteps, items, interactions, categories]);
}

// EVERY HOLE OF ONE PART, over the whole manual — what Phase 5 shows beside the mesh it will
// eventually drill, and what a person checks the reconstruction against.
//
// KEYED BY THE PART AND NOT BY THE GESTURE: a
// mesh is one part and the holes in it were found in four different steps. Each comes back
// with the step that found it, since "which gesture told us this" is the first thing a reader
// asks of a bore they disagree with.
//
// DEDUPED BY ID, since a hole is declared once and worked in many times.
//
// EVERY HOLE OF THE PART, AND THERE IS ONLY ONE SET OF THEM. A mirror pair is one part
// modelled once: the holes are the LEFT unit's and the right unit is that same file reflected
// (see StepHole.side, which argues it, and PartViewer, which draws it). So there is no hand to
// ask about — this took one, and filtered on it, back when a hole carried the frame it was
// read in. What that cost: a bore the manual showed on the right rail was skipped by the left
// hand as somebody else's and refused by the right hand, which cannot be drilled at all, so it
// was never cut and nothing said so.
//
// `side` survives on the hole, and is shown, as WHICH UNIT THE MANUAL BORES IT IN — a sentence
// for the reader. It is not a filter and must not become one again.
export function holesOfPart(
  byStep: ByHoles,
  partId: string,
): { hole: StepHole; category: string }[] {
  const out: { hole: StepHole; category: string }[] = [];
  for (const [category, found] of Object.entries(byStep)) {
    for (const h of found.holes) {
      if (h.part !== partId) continue;
      if (out.some((k) => k.hole.id === h.id)) continue;
      out.push({ hole: h, category });
    }
  }
  return out;
}
